import React, { useEffect, useState } from 'react';
import { jsPDF as JsPDF } from 'jspdf';
import { defaults, Chart, registerables } from 'chart.js/auto';
import { Line, Bar } from 'react-chartjs-2';
import { View, Text } from '@aws-amplify/ui-react';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Menu, Button, ButtonGroup } from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import './reports.css';
import { StyledMenuItem } from './StyledInputs';
import BulkDownloadModal from './BulkDownloadModal';
import HourlyTableView from './HourlyTableView';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';

Chart.register(...registerables);
const verticalLinePlugin = {
  id: 'verticalLine',
  afterDraw: (chart, args, options) => {
    const { ctx, scales, chartArea } = chart;
    if (options.lineAt) {
      const x = scales.x.getPixelForValue(options.lineAt);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.lineWidth = options.lineWidth || 1;
      ctx.strokeStyle = options.lineColor || 'black';
      ctx.stroke();
      ctx.restore();
    }
  },
};

// Register the plugin
Chart.register(verticalLinePlugin);
defaults.maintainAspectRatio = false;
defaults.responsive = true;

defaults.plugins.title.display = true;
defaults.plugins.title.align = 'center';
defaults.plugins.title.font.size = 25;

const colorArray = [
  '#551AC9',
  '#00C572',
  '#EF3D5D',
  '#438EF7',
  '#FFCA54',
  '#703DDD',
  '#3AC6E5',
  '#B41632',
  '#0A157B',
  '#FF9F43',
  '#17D870',
  '#D42645',
  '#01A579',
  '#350A7B',
  '#F98A27',
  '#13DCA6',
  '#173FA9',
  '#9B0620',
  '#2E6EEA',
  '#A267EE',
  '#00D2D3',
  '#9b59b6',
  '#3498db',
  '#2ecc71',
  '#f1c40f',
  '#e74c3c',
  '#34495e',
  '#95a5a6',
  '#d35400',
  '#16a085',
];

const optionsObject = (title, label) => ({
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: function (val) {
          if (val % 1 === 0) return val;
        },
      },
    },
  },
  plugins: {
    title: {
      text: title,
    },
    verticalLine: {
      lineAt: label,
      lineColor: 'red',
      lineWidth: 2,
    },
  },
  elements: {
    line: {
      tension: 0.2,
    },
  },
  spanGaps: true,
});

// Add this wrapped chart component with memoization to prevent re-renders
const MemoizedChartComponent = React.memo(
  ({ report, teamColorMap }) => {
    const chartRef = React.useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
      // Small timeout to stagger chart initialization
      const timer = setTimeout(() => {
        setIsLoaded(true);
      }, 50);

      return () => clearTimeout(timer);
    }, []);

    // Until loaded, show placeholder
    if (!isLoaded) {
      return (
        <Box
          style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Box
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid var(--primary-color)',
              animation: 'spin 1s linear infinite',
            }}
          />
          <Text style={{ marginTop: '10px' }}>Preparing chart...</Text>
        </Box>
      );
    }

    const chartData = {
      labels: report.labels,
      datasets: report.data_sets
        .sort((a, b) => a.label.localeCompare(b.label))
        .map((ds) => ({
          ...ds,
          backgroundColor: teamColorMap[ds.label] || colorArray[0],
          borderColor: teamColorMap[ds.label] || colorArray[0],
        })),
    };

    const chartOptions = optionsObject(
      `${report.report_info.title} Report`,
      report.report_info.today_label
    );
    const ChartComponent = report.labels.length > 1 ? Line : Bar;

    return (
      <ChartComponent
        id={report.report_info.id}
        ref={chartRef}
        data={chartData}
        options={chartOptions}
        // Add key to prevent chart library from trying to update existing chart
        key={`chart-${report.report_info.id}`}
      />
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Only re-render if the report ID changes
    return prevProps.report.report_info.id === nextProps.report.report_info.id;
  }
);

// Function to capture chart image and resolve promise
function captureAndResolve(canvas, chart, resolve, reject) {
  try {
    const imageUrl = canvas.toDataURL('image/png');
    chart.destroy();
    resolve({ imageUrl, chart });
  } catch (err) {
    console.error('Error capturing chart image:', err);
    chart.destroy();
    reject(err);
  }
}

// Add display name
MemoizedChartComponent.displayName = 'MemoizedChartComponent';

// Update the createChartImage function to wait longer
const createChartImage = async (canvas, chartType, chartData, chartOptions) => {
  const ctx = canvas.getContext('2d');

  return new Promise((resolve, reject) => {
    try {
      // Create the chart
      const chart = new Chart(ctx, {
        type: chartType,
        data: chartData,
        options: chartOptions,
      });

      // Set up a timeout in case animation never completes
      const renderTimeout = setTimeout(() => {
        captureAndResolve(canvas, chart, resolve, reject);
      }, 4000);

      // Handler for animation completion
      chart.options.animation = {
        ...chart.options.animation,
        onComplete: () => {
          // Add an ADDITIONAL delay after animation completes to ensure rendering is stable
          setTimeout(() => {
            clearTimeout(renderTimeout);
            captureAndResolve(canvas, chart, resolve, reject);
          }, 750); // Wait 500ms after animation completes before capturing
        },
      };
    } catch (err) {
      console.error('Error creating chart:', err);
      reject(err);
    }
  });
};

// Add this utility function for waiting on chart rendering with improved animation handling
const waitForChartRender = async (chart, canvas) => {
  // With animations disabled, we only need a minimal wait
  await new Promise((resolve) => setTimeout(resolve, 300));

  // For safety, check if canvas has content
  try {
    const pixels = canvas.getContext('2d').getImageData(0, 0, 10, 10).data;
    const hasContent = pixels.some((val) => val > 0);
    if (!hasContent) {
      // If no content detected, wait a bit longer
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  } catch (e) {
    console.warn('Error checking chart render status:', e);
  }

  return true;
};

export default function ReportsCharts(props) {
  const { reports, filters } = props;
  const { theme } = useTheme();
  const [reportsList, setReportsList] = useState(null);
  const [teamColorMap, setTeamColorMap] = useState({});
  const [previousReportType, setPreviousReportType] = useState(null);
  const [bulkDownloadModalOpen, setBulkDownloadModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeReportId, setActiveReportId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('graph'); // 'graph' or 'table'

  // Separate chart limits for different report types
  const hourlyChartsPerPage = 10;
  const userChartsPerPage = 4; // Much smaller limit for user reports

  // Add state to track page transition
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [pageTransitionTimeout, setPageTransitionTimeout] = useState(null);
  const [targetPage, setTargetPage] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update chart title color based on theme
  useEffect(() => {
    defaults.plugins.title.color = theme.text;
  }, [theme]);

  useEffect(() => {
    // Check if report type has changed
    if (
      reports &&
      previousReportType &&
      reports.report_type !== previousReportType
    ) {
      // Clear the reports list when report type changes
      setReportsList(null);
      return;
    }

    // Update previous report type
    if (reports) {
      setPreviousReportType(reports.report_type);
    }

    if (reports) {
      const reportsData = Array.isArray(reports.reports)
        ? reports.reports
        : Object.values(reports.reports);

      setReportsList(reportsData);

      // Create a consistent color mapping for all teams
      const allTeams = new Set();
      reportsData.forEach((report) => {
        report.data_sets.forEach((dataset) => {
          allTeams.add(dataset.label);
        });
      });

      // Sort team names alphabetically to ensure consistent ordering
      const sortedTeams = Array.from(allTeams).sort();

      // Create and store the color mapping
      const colorMapping = {};
      sortedTeams.forEach((teamName, index) => {
        colorMapping[teamName] = colorArray[index % colorArray.length];
      });

      setTeamColorMap(colorMapping);
    }
  }, [reports, previousReportType]);

  const createExportName = (report, format) => {
    let fileName = report.report_info.title.split(' ').join('_') + '_';
    if (reports.report_type !== 'hourly') {
      fileName += `${report.labels[0].split(' ')[1]}-${
        report.labels[report.labels.length - 1].split(' ')[1]
      }_`;
    }
    fileName += `report_${reports.org_id}.${format}`;
    return fileName;
  };

  // Add a helper function to add report metadata to PDFs
  const addReportMetadata = (pdf, margin) => {
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    const today = new Date();
    const dateStr = today.toLocaleDateString();
    const timeStr = today.toLocaleTimeString();
    pdf.text(`Generated on ${dateStr} at ${timeStr}`, margin, margin);
    return margin + 10;
  };

  // Add this function to download a single chart as PDF
  const downloadChartAsPDF = async (report) => {
    try {
      // Create PDF document with single chart
      const pdf = new JsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 10;

      // Create a div container for the charts
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.visibility = 'hidden';
      document.body.appendChild(tempContainer);

      // Add metadata and timestamp to PDF
      const newMargin = addReportMetadata(pdf, margin);

      // Add title
      pdf.setFontSize(18);
      pdf.text(report.report_info.title, pageWidth / 2, newMargin + 15, {
        align: 'center',
      });

      // Create canvas for chart
      const canvas = document.createElement('canvas');
      canvas.width = 800;
      canvas.height = 400;
      tempContainer.appendChild(canvas);

      // Get chart type from report info if available
      let chartType = 'bar';
      if (report.labels.length > 1) {
        chartType = 'line';
      }

      // Create datasets for the chart with proper colors
      const datasets = report.data_sets.map((dataset, index) => {
        return {
          ...dataset,
          backgroundColor:
            teamColorMap[dataset.label] ||
            colorArray[index % colorArray.length],
          borderColor:
            teamColorMap[dataset.label] ||
            colorArray[index % colorArray.length],
        };
      });

      // Create chart data
      const chartData = {
        labels: report.labels,
        datasets,
      };

      // IMPORTANT: Use await here to make sure we get the image data
      const { imageUrl } = await createChartImage(
        canvas,
        chartType,
        chartData,
        optionsObject(report.report_info.title, report.report_info.today_label)
      );

      // Add chart to PDF
      pdf.addImage(
        imageUrl,
        'PNG',
        margin,
        newMargin + 30,
        pageWidth - margin * 2,
        (pageWidth - margin * 2) * 0.6
      );

      // Save the PDF with a meaningful filename
      const filename = createExportName(report, 'pdf');
      pdf.save(filename);

      // Clean up
      tempContainer.remove();
    } catch (error) {
      console.error('Error generating PDF for single chart:', error);
    }
  };

  // Function to handle single CSV export - for hourly reports, get detailed records like "Merge and Export Reports"
  const downloadSingleCSV = async (report) => {
    try {
      // For hourly reports, fetch detailed records using the same API as "Merge and Export Reports"
      if (reports.report_type === 'hourly') {
        // Use the same API call structure as exportFullReportCSV in ReportsScreen.js
        const userIDs =
          filters && filters.checkedUsers
            ? Object.keys(filters.checkedUsers)
            : [];

        // Get the date range from the report title and chart context
        // For hourly reports, the title usually contains the date like "2025-10-09 Pin Dropping"
        const reportTitle = report.report_info.title;
        const chartLabels = report.labels || [];
        let fromDate, toDate;

        // Extract date from report title (format: "YYYY-MM-DD Something")
        const dateMatch = reportTitle.match(/(\d{4}-\d{2}-\d{2})/);
        if (dateMatch) {
          const reportDate = dateMatch[1]; // e.g., "2025-10-09"
          const [year, month, day] = reportDate.split('-').map(Number);
          fromDate = new Date(year, month - 1, day);
          toDate = new Date(year, month - 1, day);

          // Use chart labels to get the time range
          if (chartLabels.length > 0) {
            const firstTime = chartLabels[0]; // e.g., "08:00"
            const lastTime = chartLabels[chartLabels.length - 1]; // e.g., "13:00"

            // Parse the times and create proper date objects
            const [firstHour, firstMin] = firstTime.split(':').map(Number);
            const [lastHour, lastMin] = lastTime.split(':').map(Number);

            // Create dates explicitly with the time components
            fromDate = new Date(
              year,
              month - 1,
              day,
              firstHour,
              firstMin || 0,
              0,
              0
            );
            toDate = new Date(
              year,
              month - 1,
              day,
              lastHour,
              lastMin || 0,
              0,
              0
            );
          } else {
            // Fallback to full day if no time labels
            fromDate = new Date(year, month - 1, day, 0, 0, 0, 0);
            toDate = new Date(year, month - 1, day, 23, 59, 59, 999);
          }
        } else {
          // Fallback to today if we can't parse the date
          fromDate = new Date();
          fromDate.setHours(0, 0, 0, 0);
          toDate = new Date();
          toDate.setHours(23, 59, 59, 999);
        }

        const fromTimeUTC = new Date(
          fromDate.getTime() - fromDate.getTimezoneOffset() * 60000
        );
        const toTimeUTC = new Date(
          toDate.getTime() - toDate.getTimezoneOffset() * 60000
        );

        const payload = {
          report_type: 'hourly-export',
          org_id: reports.org_id,
          users: userIDs,
          dates: {
            from: fromTimeUTC.toISOString(),
            to: toTimeUTC.toISOString(),
          },
          pageSize: 1000,
        };

        const response = await fetch(
          'https://api.gogamify.com/report/kpireport',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const data = await response.json();

          if (data.records && data.records.length > 0) {
            console.log('Sample record:', data.records[0]);
            // Use the exact same CSV format as "Merge and Export Reports"
            let csvString = `,User Name,Date,Time,Pin,Address,Distance From Door\r\n`;

            data.records.forEach((record, i) => {
              const addressWithCommas = `"${record.address || 'N/A'}"`;
              csvString += `${i + 1},${record.user_name || 'Unknown'},${
                record.pin_date || ''
              },${record.pin_time || ''},${
                record.pin || ''
              },${addressWithCommas},${
                record.distance_from_door || 'Not Recorded'
              }\r\n`;
            });

            const filename = `${report.report_info.title.replace(
              /\s+/g,
              '_'
            )}_detailed_records_${reports.org_id}.csv`;

            const blob = new Blob([csvString], {
              type: 'text/csv;charset=utf-8;',
            });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return; // Successfully exported detailed records
          }
        }
      }

      // For non-hourly reports or if API call fails, export chart data
      let csvString = 'Date,Team,Value\r\n';

      report.data_sets.forEach((dataset) => {
        const teamName = dataset.label;
        dataset.data.forEach((value, index) => {
          if (value !== null && value !== undefined) {
            const date = report.labels[index];
            csvString += `"${date}","${teamName}",${value}\r\n`;
          }
        });
      });

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', createExportName(report, 'csv'));
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting CSV:', error);

      // Fallback to chart data
      let csvString = 'Date,Team,Value\r\n';
      report.data_sets.forEach((dataset) => {
        const teamName = dataset.label;
        dataset.data.forEach((value, index) => {
          if (value !== null && value !== undefined) {
            const date = report.labels[index];
            csvString += `"${date}","${teamName}",${value}\r\n`;
          }
        });
      });

      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', createExportName(report, 'csv'));
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClick = (e, reportId) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setActiveReportId(reportId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setActiveReportId(null);
  };

  // Update the downloadAllChartsAsPDF function to handle comparison charts

  const downloadAllChartsAsPDF = async (
    customReports = reports,
    customColorMap = teamColorMap,
    customFilename = null
  ) => {
    const reportData = customReports || reports;
    const colorMap = customColorMap || teamColorMap;

    // Create loading indicator (same as before)
    const loadingElement = document.createElement('div');
    loadingElement.style.position = 'fixed';
    loadingElement.style.top = '0';
    loadingElement.style.left = '0';
    loadingElement.style.width = '100%';
    loadingElement.style.height = '100%';
    loadingElement.style.backgroundColor = 'rgba(0,0,0,0.5)';
    loadingElement.style.zIndex = '9999';
    loadingElement.style.display = 'flex';
    loadingElement.style.justifyContent = 'center';
    loadingElement.style.alignItems = 'center';
    loadingElement.style.flexDirection = 'column';
    loadingElement.innerHTML = `
      <div style="width: 50px; height: 50px; border: 5px solid var(--primary-color); border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
      <div style="color: white; margin-top: 20px; font-size: 20px;">Generating PDF...</div>
    `;

    // Add a progress indicator to the loading element
    const progressText = document.createElement('div');
    progressText.style.color = 'white';
    progressText.style.marginTop = '10px';
    progressText.textContent = 'Preparing charts...';
    loadingElement.appendChild(progressText);

    document.body.appendChild(loadingElement);

    // Add animation style
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleElement);

    const pdf = new JsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 10;

    // Create a div container for the charts
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.width = '800px';
    tempContainer.style.height = '400px';
    tempContainer.style.zIndex = '-1000';
    document.body.appendChild(tempContainer);

    try {
      if (reportData.report_type === 'team') {
        // Add timestamp and report info
        const newMargin = addReportMetadata(pdf, margin);
        pdf.setFontSize(22);
        pdf.text('Team KPI Reports', pageWidth / 2, newMargin + 10, {
          align: 'center',
        });

        // IMPORTANT: Handle both array and object formats for reports
        const reportsArray = Array.isArray(reportData.reports)
          ? reportData.reports
          : Object.values(reportData.reports);

        // Calculate total charts for progress tracking
        const totalCharts = reportsArray.length;

        // PROCESS ALL KPI CHARTS
        let currentY = newMargin + 20;
        let firstChart = true;

        for (let i = 0; i < reportsArray.length; i++) {
          const report = reportsArray[i];
          progressText.textContent = `Processing chart
            ${i + 1}
          of ${totalCharts}`;

          // Add a new page if needed
          if (
            !firstChart &&
            currentY > pdf.internal.pageSize.getHeight() - 120
          ) {
            pdf.addPage();
            currentY = margin + 10;
          }

          // Add chart title
          pdf.setFontSize(14);
          pdf.text(report.report_info.title, pageWidth / 2, currentY, {
            align: 'center',
          });
          currentY += 10;

          // Create and configure the chart
          const canvas = document.createElement('canvas');
          canvas.width = 800;
          canvas.height = 400;
          canvas.style.width = '800px';
          canvas.style.height = '400px';
          tempContainer.innerHTML = ''; // Clear previous charts
          tempContainer.appendChild(canvas);

          // Set up datasets with proper colors
          const datasets = report.data_sets.map((dataset, idx) => ({
            ...dataset,
            backgroundColor:
              colorMap[dataset.label] || colorArray[idx % colorArray.length],
            borderColor:
              colorMap[dataset.label] || colorArray[idx % colorArray.length],
          }));

          // Create chart data object
          const chartData = {
            labels: report.labels,
            datasets,
          };

          // Determine chart type
          const chartType = chartData?.labels?.length > 1 ? 'line' : 'bar';

          try {
            // Create the chart with animations disabled
            const chart = new Chart(canvas.getContext('2d'), {
              type: chartType,
              data: chartData,
              options: {
                ...optionsObject(
                  report.report_info.title,
                  report.report_info.today_label
                ),
                animation: false, // Completely disable all animations
                responsive: false, // Prevent resize issues
                devicePixelRatio: 2, // Higher quality
              },
            });

            // Minimal wait since animations are disabled
            progressText.textContent = `Rendering chart
              ${i + 1}
            of ${totalCharts}...`;
            await waitForChartRender(chart, canvas);

            // Now capture the image
            progressText.textContent = `Capturing chart
              ${i + 1}
            of ${totalCharts}...`;
            const imageUrl = canvas.toDataURL('image/png', 1.0); // Use max quality

            // Calculate image dimensions for PDF
            const imgWidth = pageWidth - margin * 2;
            const imgHeight = imgWidth * 0.5; // Maintain aspect ratio

            // Add image to PDF
            pdf.addImage(
              imageUrl,
              'PNG',
              margin,
              currentY,
              imgWidth,
              imgHeight
            );
            // Update position for next chart
            currentY += imgHeight + 20;
            firstChart = false;

            // Clean up
            chart.destroy();
          } catch (chartError) {
            console.error(`Error rendering chart ${i}:`, chartError);
            // Add error message to PDF
            pdf.setTextColor(255, 0, 0);
            pdf.text(
              `Error rendering chart: ${report.report_info.title}`,
              margin,
              currentY + 20
            );
            pdf.setTextColor(0, 0, 0);
            currentY += 40;
          }

          // Give browser time to clean up resources
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        // Save the PDF
        progressText.textContent = 'Saving PDF...';
        const filename =
          customFilename || `Team_KPI_Reports_${reportData.org_id}.pdf`;
        pdf.save(filename);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Show an error message to the user
      const errorMessage = document.createElement('div');
      errorMessage.style.color = 'red';
      errorMessage.style.marginTop = '10px';
      errorMessage.style.fontSize = '16px';
      errorMessage.textContent = 'Error generating PDF. Please try again.';
      loadingElement.appendChild(errorMessage);

      // Remove error message after 3 seconds
      setTimeout(() => {
        try {
          loadingElement.removeChild(errorMessage);
        } catch (e) {
          // Already removed, ignore
        }
      }, 3000);
    } finally {
      // Clean up
      if (tempContainer) {
        tempContainer.remove();
      }
      document.body.removeChild(loadingElement);
      document.head.removeChild(styleElement);
    }
  };

  const downloadAllOrgChartsAsPDF = async (
    customReports = reports,
    customFilename = null
  ) => {
    const reportData = customReports || reports;

    // Create a loading indicator element
    const loadingElement = document.createElement('div');
    loadingElement.style.position = 'fixed';
    loadingElement.style.top = '0';
    loadingElement.style.left = '0';
    loadingElement.style.width = '100%';
    loadingElement.style.height = '100%';
    loadingElement.style.backgroundColor = 'rgba(0,0,0,0.5)';
    loadingElement.style.zIndex = '9999';
    loadingElement.style.display = 'flex';
    loadingElement.style.justifyContent = 'center';
    loadingElement.style.alignItems = 'center';
    loadingElement.style.flexDirection = 'column';
    loadingElement.innerHTML = `
      <div style="width: 50px; height: 50px; border: 5px solid var(--primary-color); border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite;"></div>
      <div style="color: white; margin-top: 20px; font-size: 20px;">Generating PDF...</div>
    `;

    // Add a progress indicator to the loading element
    const progressText = document.createElement('div');
    progressText.style.color = 'white';
    progressText.style.marginTop = '10px';
    progressText.textContent = 'Preparing charts...';
    loadingElement.appendChild(progressText);

    document.body.appendChild(loadingElement);

    // Add animation style
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleElement);

    const pdf = new JsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;

    // Create a div container for the charts
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.width = '800px';
    tempContainer.style.height = '400px';
    tempContainer.style.zIndex = '-1000';
    document.body.appendChild(tempContainer);

    try {
      // Add title and metadata
      const newMargin = addReportMetadata(pdf, margin);
      pdf.setFontSize(22);
      pdf.text('Organization KPI Reports', pageWidth / 2, newMargin + 10, {
        align: 'center',
      });

      // IMPORTANT: Handle both array and object formats for reports
      const reportsArray = Array.isArray(reportData.reports)
        ? reportData.reports
        : Object.values(reportData.reports);

      const totalReports = reportsArray.length;

      // Process each report - one per page
      for (let index = 0; index < reportsArray.length; index++) {
        const report = reportsArray[index];

        // Update progress text
        progressText.textContent = `Processing chart
          ${index + 1}
        of ${totalReports}`;

        // Add a new page for charts after the first one
        if (index > 0) {
          pdf.addPage();
          // Add the metadata to each page
          addReportMetadata(pdf, margin);
        }

        // Add chart title
        pdf.setFontSize(16);
        pdf.text(report.report_info.title, pageWidth / 2, margin + 25, {
          align: 'center',
        });

        // Create canvas for chart
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 400;
        canvas.style.width = '800px';
        canvas.style.height = '400px';
        tempContainer.innerHTML = ''; // Clear previous contents
        tempContainer.appendChild(canvas);

        // Prepare chart data
        const chartData = {
          labels: report.labels,
          datasets: report.data_sets.map((dataset, idx) => ({
            ...dataset,
            backgroundColor: colorArray[idx % colorArray.length],
            borderColor: colorArray[idx % colorArray.length],
          })),
        };

        // Determine chart type
        const chartType = chartData?.labels?.length > 1 ? 'line' : 'bar';

        try {
          // Create the chart with animations disabled
          const chart = new Chart(canvas.getContext('2d'), {
            type: chartType,
            data: chartData,
            options: {
              ...optionsObject(
                report.report_info.title,
                report.report_info.today_label
              ),
              animation: false, // Completely disable all animations
              responsive: false, // Prevent resize issues
              devicePixelRatio: 2, // Higher quality
            },
          });

          // Minimal wait since animations are disabled
          progressText.textContent = `Rendering chart
            ${index + 1}
          of ${totalReports}...`;
          await waitForChartRender(chart, canvas);

          // Now capture the image
          progressText.textContent = `Capturing chart
            ${index + 1}
          of ${totalReports}...`;
          const imageUrl = canvas.toDataURL('image/png', 1.0); // Use max quality

          // Calculate image dimensions
          const imgWidth = pageWidth - margin * 2;
          const imgHeight = imgWidth * 0.6; // Maintain aspect ratio

          // Add image to PDF
          pdf.addImage(
            imageUrl,
            'PNG',
            margin,
            margin + 35,
            imgWidth,
            imgHeight
          );

          // Clean up
          chart.destroy();
        } catch (chartError) {
          console.error(`Error rendering org chart ${index}:`, chartError);
          // Add error message to PDF
          pdf.setTextColor(255, 0, 0);
          pdf.text(
            `Error rendering chart: ${report.report_info.title}`,
            margin,
            margin + 45
          );
          pdf.setTextColor(0, 0, 0);
        }

        // Give browser time to clean up resources
        await new Promise((resolve) => setTimeout(resolve, 300));
      }

      // Update progress for saving
      progressText.textContent = 'Saving PDF...';

      // Save the PDF with appropriate filename
      const filename =
        customFilename || `Organization_KPI_Reports_${reportData.org_id}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating organization PDF:', error);
      // Show an error message
      const errorMessage = document.createElement('div');
      errorMessage.style.color = 'red';
      errorMessage.style.marginTop = '10px';
      errorMessage.style.fontSize = '16px';
      errorMessage.textContent = 'Error generating PDF. Please try again.';
      loadingElement.appendChild(errorMessage);

      // Remove error message after 3 seconds
      setTimeout(() => {
        try {
          loadingElement.removeChild(errorMessage);
        } catch (e) {
          // Already removed, ignore
        }
      }, 3000);
    } finally {
      // Clean up
      if (tempContainer) {
        tempContainer.remove();
      }
      // Remove loading element
      document.body.removeChild(loadingElement);
      document.head.removeChild(styleElement);
    }
  };

  // Function to handle page changes with a smoother transition
  const handlePageChange = (newPage) => {
    if (isPageChanging) return;

    setTargetPage(newPage);
    setIsPageChanging(true);

    if (pageTransitionTimeout) {
      clearTimeout(pageTransitionTimeout);
    }

    // First phase: fade out current page
    const timeout = setTimeout(() => {
      // Change the page
      setCurrentPage(newPage);

      // Second phase: fade in new page after a short delay
      setTimeout(() => {
        setIsPageChanging(false);
        setTargetPage(null);
      }, 300);

      window.scrollTo(0, 0);
    }, 300);

    setPageTransitionTimeout(timeout);
  };

  // Add effect to clear "initial load" state once first render is complete
  useEffect(() => {
    if (reportsList && isInitialLoad) {
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, 1000); // Time for initial charts to stabilize

      return () => clearTimeout(timer);
    }
  }, [reportsList, isInitialLoad]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (pageTransitionTimeout) {
        clearTimeout(pageTransitionTimeout);
      }
    };
  }, [pageTransitionTimeout]);

  // Calculate pagination values
  const needsPagination =
    (reports && reports.report_type === 'hourly') ||
    reports.report_type === 'user';
  const chartsPerPage =
    reports && reports.report_type === 'user'
      ? userChartsPerPage
      : hourlyChartsPerPage;
  const totalPages =
    needsPagination && reportsList
      ? Math.ceil(reportsList.length / chartsPerPage)
      : 1;

  // For reports that need pagination, paginate the data; otherwise use all data
  const paginatedReports =
    needsPagination && reportsList
      ? reportsList.slice(
          (currentPage - 1) * chartsPerPage,
          currentPage * chartsPerPage
        )
      : reportsList;

  // Define the page transition style
  const pageTransitionStyle = isPageChanging
    ? {
        opacity: 0.7,
        transition: 'opacity 0.2s ease-in-out',
        pointerEvents: 'none',
      }
    : {};

  if (!reportsList || reportsList.length === 0)
    return (
      <View style={{ width: '100%' }}>
        <Box
          style={{
            width: '100%',
            height: '30dvh',
            display: 'flex',
            justifyContent: 'center',
            paddingTop: '5rem',
          }}
        >
          <Text style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
            {'No data is available for this report'}
          </Text>
        </Box>
      </View>
    );

  return (
    <View style={{ width: '100%' }}>
      {/* Add style definition for the spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* View Toggle for Hourly Reports */}
      {reports && reports.report_type === 'hourly' && (
        <Box
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '20px 0',
          }}
        >
          <ButtonGroup variant="contained" aria-label="view mode toggle">
            <Button
              onClick={() => setViewMode('graph')}
              style={{
                backgroundColor:
                  viewMode === 'graph'
                    ? 'var(--button-color)'
                    : 'var(--background-color-2)',
                color:
                  viewMode === 'graph'
                    ? 'var(--text-cta)'
                    : 'var(--text-color)',
                fontWeight: 'bold',
                padding: '10px 20px',
              }}
              startIcon={<BarChartIcon />}
            >
              Graph View
            </Button>
            <Button
              onClick={() => setViewMode('table')}
              style={{
                backgroundColor:
                  viewMode === 'table'
                    ? 'var(--button-color)'
                    : 'var(--background-color-2)',
                color:
                  viewMode === 'table'
                    ? 'var(--text-cta)'
                    : 'var(--text-color)',
                fontWeight: 'bold',
                padding: '10px 20px',
              }}
              startIcon={<TableChartIcon />}
            >
              Table View
            </Button>
          </ButtonGroup>
        </Box>
      )}

      {/* Loading overlay for initial load */}
      {isInitialLoad && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 900,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'var(--background-color-2)',
              padding: '20px',
              borderRadius: '10px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Box
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid var(--primary-color)',
                animation: 'spin 1s linear infinite',
              }}
            />
            <Text
              style={{
                marginTop: '15px',
                fontWeight: 'bold',
                fontSize: '16px',
              }}
            >
              Preparing charts...
            </Text>
          </Box>
        </Box>
      )}

      {/* Overlay loading indicator for page transitions */}
      {isPageChanging && (
        <Box
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '20px',
            borderRadius: '10px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid var(--primary-color)',
              animation: 'spin 1s linear infinite',
            }}
          />
          <Text style={{ marginTop: '10px', fontWeight: 'bold' }}>
            Loading page {targetPage}...
          </Text>
        </Box>
      )}

      {reports &&
        (reports.report_type === 'team' || reports.report_type === 'org') &&
        reportsList &&
        reportsList.length > 0 && (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              margin: '10px 5px',
            }}
          >
            {/* Bulk Download button */}
            <Box
              className="download-all-button hov"
              onClick={() => setBulkDownloadModalOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 16px',
                backgroundColor: 'var(--button-color)',
                color: 'var(--text-cta)',
                borderRadius: '8px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
                width: 'fit-content',
              }}
            >
              <DownloadIcon style={{ marginRight: '8px' }} />
              Bulk Download
            </Box>
          </Box>
        )}

      {/* Replace the CSV export modal with the bulk download modal */}
      {bulkDownloadModalOpen && (
        <BulkDownloadModal
          open={bulkDownloadModalOpen}
          onClose={() => setBulkDownloadModalOpen(false)}
          reports={reports}
          teamColorMap={teamColorMap}
          downloadAllChartsAsPDF={downloadAllChartsAsPDF}
          downloadAllOrgChartsAsPDF={downloadAllOrgChartsAsPDF}
          filters={filters}
        />
      )}

      {/* Show Table View for Hourly Reports */}
      {reports && reports.report_type === 'hourly' && viewMode === 'table' && (
        <HourlyTableView
          reports={reports}
          userIds={Object.keys(filters.checkedUsers || {})}
        />
      )}

      {/* Show Graph View (default for all reports) */}
      {(!reports ||
        reports.report_type !== 'hourly' ||
        viewMode === 'graph') && (
        <Box className="reports-container" style={pageTransitionStyle}>
          {/* Use paginatedReports instead of reportsList */}
          {paginatedReports &&
            paginatedReports.map((report) => (
              <Box
                key={`container-${report.report_info.id}`}
                className="single-chart-container"
              >
                <Box
                  onClick={(e) => handleClick(e, report.report_info.id)}
                  className="download-report-button hov"
                >
                  <DownloadIcon />
                  <Menu
                    id={`menu-${report.report_info.id}`}
                    anchorEl={
                      report.report_info.id === activeReportId ? anchorEl : null
                    }
                    open={report.report_info.id === activeReportId}
                    onClose={handleClose}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <StyledMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadSingleCSV(report);
                        handleClose();
                      }}
                    >
                      Export as CSV
                    </StyledMenuItem>
                    <StyledMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadChartAsPDF(report);
                        handleClose();
                      }}
                    >
                      Export as PDF
                    </StyledMenuItem>
                  </Menu>
                </Box>
                <MemoizedChartComponent
                  report={report}
                  teamColorMap={teamColorMap}
                />
              </Box>
            ))}
        </Box>
      )}

      {/* Add pagination controls for reports that need it (only for graph view) */}
      {needsPagination && totalPages > 1 && viewMode === 'graph' && (
        <Box
          className="pagination-controls"
          style={{
            display: 'flex',
            justifyContent: 'center',
            margin: '20px 0',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Box
            className={`pagination-button hov ${
              currentPage === 1 || isPageChanging ? 'disabled' : ''
            }`}
            onClick={() =>
              currentPage > 1 &&
              !isPageChanging &&
              handlePageChange(currentPage - 1)
            }
            style={{
              padding: '8px 16px',
              backgroundColor:
                currentPage === 1 || isPageChanging
                  ? 'var(--border-color)'
                  : 'var(--button-color)',
              color:
                currentPage === 1 || isPageChanging
                  ? 'var(--text-color-light)'
                  : 'var(--text-cta)',
              borderRadius: '4px',
              cursor:
                currentPage === 1 || isPageChanging ? 'default' : 'pointer',
              fontWeight: 'bold',
            }}
          >
            Previous
          </Box>

          <Box style={{ fontWeight: 'bold' }}>
            Page {currentPage} of {totalPages}
          </Box>

          <Box
            className={`pagination-button hov ${
              currentPage === totalPages || isPageChanging ? 'disabled' : ''
            }`}
            onClick={() =>
              currentPage < totalPages &&
              !isPageChanging &&
              handlePageChange(currentPage + 1)
            }
            style={{
              padding: '8px 16px',
              backgroundColor:
                currentPage === totalPages || isPageChanging
                  ? 'var(--border-color)'
                  : 'var(--button-color)',
              color:
                currentPage === totalPages || isPageChanging
                  ? 'var(--text-color-light)'
                  : 'var(--text-cta)',
              borderRadius: '4px',
              cursor:
                currentPage === totalPages || isPageChanging
                  ? 'default'
                  : 'pointer',
              fontWeight: 'bold',
            }}
          >
            Next
          </Box>
        </Box>
      )}

      {/* Show total charts info for reports that need pagination (only for graph view) */}
      {needsPagination &&
        reportsList &&
        reportsList.length > 0 &&
        viewMode === 'graph' && (
          <Box
            style={{
              textAlign: 'center',
              marginBottom: '20px',
              color: 'var(--text-color-light)',
            }}
          >
            {`Showing ${(currentPage - 1) * chartsPerPage + 1}-${Math.min(
              currentPage * chartsPerPage,
              reportsList.length
            )} of
          ${reportsList.length} charts`}
            {reports.report_type === 'user' && (
              <Box
                style={{
                  marginTop: '10px',
                  fontSize: '0.9rem',
                  color: 'var(--primary-color)',
                }}
              >
                Limited to {userChartsPerPage} charts per page to improve
                performance
              </Box>
            )}
          </Box>
        )}
    </View>
  );
}
